/**
 * CSRF Token Management Utility
 * 
 * This utility manages CSRF tokens for protecting against Cross-Site Request Forgery attacks.
 * The token is fetched from the server and included in all POST requests.
 */

import axios from 'axios';

let csrfTokenCache = null;
let tokenFetchPromise = null;

/**
 * Fetches CSRF token from the server
 * @returns {Promise<string>} CSRF token
 */
async function fetchCSRFToken() {
  // If we already have a cached token, return it
  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  // If a fetch is already in progress, wait for it
  if (tokenFetchPromise) {
    return tokenFetchPromise;
  }

  // Create a new fetch promise using axios directly to avoid circular dependency
  tokenFetchPromise = axios
    .get('/HiringCenter/csrf-token', {
      withCredentials: true // Include cookies in the request
    })
    .then((response) => {
      if (response.data && response.data.csrfToken) {
        csrfTokenCache = response.data.csrfToken;
        return csrfTokenCache;
      }
      throw new Error('CSRF token not found in response');
    })
    .catch((error) => {
      // Clear the promise so we can retry
      tokenFetchPromise = null;
      console.error('Error fetching CSRF token:', error);
      // Return null to allow requests to proceed (server will handle validation)
      return null;
    });

  return tokenFetchPromise;
}

/**
 * Gets the CSRF token, fetching it if necessary
 * @returns {Promise<string|null>} CSRF token or null if unavailable
 */
export async function getCSRFToken() {
  try {
    return await fetchCSRFToken();
  } catch (error) {
    console.error('Error getting CSRF token:', error);
    return null;
  }
}

/**
 * Clears the cached CSRF token (useful after logout or token refresh)
 */
export function clearCSRFToken() {
  csrfTokenCache = null;
  tokenFetchPromise = null;
}

